import { AlertTriangle, Check, ChevronRight, Map, MapPin, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchSteps from './SearchSteps';
import { tx } from '../i18n';
import { formatDate, hasLapsed } from '../lib/format';
import type { AddressOption } from '../types/api';

export type CompareState = 'idle' | 'adding' | 'added' | 'removing';

interface AddressPickerProps {
  postcode: string;
  borough: string;
  addresses: AddressOption[];
  /** Open the free report for this address. */
  onOpenReport: (address: AddressOption) => void;
  /** Add this address to the comparison, or take it out again. */
  onToggleCompare: (address: AddressOption) => void;
  /** Where each address stands in the comparison. */
  compareState: (address: AddressOption) => CompareState;
  /** Set while a report is being opened, so the list does not accept a second click. */
  busyAddressLine?: string | null;
  /** Back to step one, with the postcode still in the box. */
  onChangePostcode: () => void;
}

/** How many rows before the list is long enough to need a "show more". */
const FIRST_BATCH = 8;

/**
 * The flat, unit or house number, which is the only part of the line that tells two neighbours
 * apart. "Flat 3, Seymour Court, Coppetts Road" is four flats' worth of identical text and one
 * word of difference, so the difference gets the weight.
 */
function splitUnit(addressLine: string): { unit: string; rest: string } {
  const parts = addressLine.split(',').map((part) => part.trim()).filter(Boolean);
  const first = parts[0] ?? addressLine;
  const looksLikeUnit = /^(flat|apartment|apt|unit|room|studio|no\.?)\s*[\w/-]+$/i.test(first)
    || /^[\w/-]{1,6}$/.test(first);
  if (looksLikeUnit && parts.length > 1) {
    return { unit: first, rest: parts.slice(1).join(', ') };
  }
  return { unit: first, rest: parts.slice(1).join(', ') };
}

/**
 * Step two: pick the exact door.
 *
 * <p>One dominant action per row. The row itself opens the report - it is the thing a reader came
 * to do, and two buttons of equal weight on every line turned the list into a wall of controls -
 * and Compare sits beside it as the quieter second choice. The list is not a scrollbox: a
 * scrollbar inside a page that also scrolls is a trap, so a long postcode shows its first eight
 * and offers the rest.
 *
 * <p>The arrow keys walk the rows and Home and End jump to the ends, so a long postcode is worked
 * through from the keyboard without tabbing past two controls per row.
 */
export default function AddressPicker({
  postcode,
  borough,
  addresses,
  onOpenReport,
  onToggleCompare,
  compareState,
  busyAddressLine = null,
  onChangePostcode,
}: AddressPickerProps) {
  const opening = busyAddressLine !== null;
  const rows = useRef<Array<HTMLButtonElement | null>>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [postcode, addresses.length]);

  /*
   * When the list arrives, go to it.
   *
   * <p>The search box is at the top of a long page and the addresses appear below the fold, so a
   * reader who has just pressed the button sees nothing change and waits for something that has
   * already happened.
   *
   * <p>Focus moves, not just the scroll position. A scroll alone is invisible to a screen reader
   * and leaves a keyboard reader's focus back in the postcode box, so their next Tab lands
   * somewhere they did not ask to be. Moving focus to the heading answers all three at once: the
   * viewport follows focus, the heading is announced, and the next Tab is the first address.
   *
   * <p>Only on the postcode, not on the list's length - adding a home to the comparison changes
   * that, and being thrown back up the page for it would be its own bug.
   */
  useEffect(() => {
    const node = heading.current;
    if (!node || addresses.length === 0) {
      return;
    }
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    // preventScroll, because scrollIntoView above has already put it where it belongs and the
    // two together jump.
    node.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode]);

  const shown = expanded ? addresses : addresses.slice(0, FIRST_BATCH);
  const hidden = addresses.length - shown.length;

  const move = (event: React.KeyboardEvent<HTMLUListElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) {
      return;
    }
    const buttons = rows.current.filter((node): node is HTMLButtonElement => node !== null);
    if (buttons.length === 0) {
      return;
    }
    const here = buttons.findIndex((node) => node === document.activeElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : here < 0
            ? 0
            : (here + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    event.preventDefault();
    buttons[next]?.focus();
  };

  return (
    <section className="card stack stack--tight">
      <SearchSteps current={2} postcode={postcode} onChangePostcode={onChangePostcode} />

      <div>
        <h2 className="card__title address-picker__heading" ref={heading} tabIndex={-1}>
          {tx(
            `Which property in ${postcode}?`,
            `${postcode} içinde hangi konut?`,
            `Który lokal pod ${postcode}?`,
            `Que imóvel em ${postcode}?`,
            `¿Qué vivienda en ${postcode}?`,
          )}
        </h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          {borough} &middot;{' '}
          {tx(
            `${addresses.length} address${addresses.length === 1 ? '' : 'es'} found. Open one to read its free report, or add homes to your comparison.`,
            `${addresses.length} adres bulundu. Ücretsiz raporu için birini açın ya da konutları karşılaştırmaya ekleyin.`,
            `Znaleziono ${addresses.length} ${addresses.length === 1 ? 'adres' : 'adresów'}. Otwórz jeden, aby przeczytać bezpłatny raport, albo dodaj mieszkania do porównania.`,
            `${addresses.length} morada${addresses.length === 1 ? '' : 's'} encontrada${addresses.length === 1 ? '' : 's'}. Abra uma para ler o relatório gratuito, ou junte casas à comparação.`,
            `${addresses.length} dirección${addresses.length === 1 ? '' : 'es'} encontrada${addresses.length === 1 ? '' : 's'}. Abre una para leer su informe gratuito, o añade viviendas a la comparación.`,
          )}
        </p>
        {/* The other question a reader has at this point: not "is this one any good" but "is this
            a decent part of town". The outcode is in their hand already. */}
        <p className="small" style={{ marginTop: 6 }}>
          <Link to={`/areas?outcodes=${encodeURIComponent(postcode.split(' ')[0] ?? postcode)}`}>
            <Map size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }} />
            {tx(
              `See ${postcode.split(' ')[0] ?? postcode} on a map instead`,
              `Bunun yerine ${postcode.split(' ')[0] ?? postcode} bölgesini haritada gör`,
              `Zobacz zamiast tego ${postcode.split(' ')[0] ?? postcode} na mapie`,
              `Ver antes ${postcode.split(' ')[0] ?? postcode} num mapa`,
              `Ver mejor ${postcode.split(' ')[0] ?? postcode} en un mapa`,
            )}
          </Link>
        </p>
      </div>

      <ul className="address-list" onKeyDown={move}>
        {shown.map((address, index) => {
          const state = compareState(address);
          const inComparison = state === 'added' || state === 'removing';
          const compareBusy = state === 'adding' || state === 'removing';
          const isOpening = busyAddressLine === address.addressLine;
          const { unit, rest } = splitUnit(address.addressLine);

          return (
            <li
              key={address.uprn ?? address.addressLine}
              className={inComparison ? 'address-option address-option--added' : 'address-option'}
              aria-busy={isOpening || compareBusy}
            >
              <button
                type="button"
                className="address-option__main"
                ref={(node) => {
                  rows.current[index] = node;
                }}
                onClick={() => onOpenReport(address)}
                disabled={opening || compareBusy}
              >
                <MapPin size={18} aria-hidden="true" className="address-option__pin" />
                <span className="address-option__text">
                  <span className="address-option__unit">{unit}</span>
                  {rest ? <span className="address-option__line">{rest}</span> : null}
                  <span className="address-option__hint">
                    {postcode}
                    {' · '}
                    {address.approximate
                      ? tx(
                          'postcode level only — no address list is available here',
                          'yalnızca posta kodu düzeyinde — burada adres listesi yok',
                          'tylko poziom kodu pocztowego — lista adresów nie jest tu dostępna',
                          'apenas ao nível do código postal — não há lista de moradas aqui',
                          'solo a nivel de código postal — aquí no hay lista de direcciones',
                        )
                      : tx(
                          'an individual address in the postcode register',
                          'posta kodu kaydındaki tekil adres',
                          'pojedynczy adres w rejestrze kodów pocztowych',
                          'uma morada individual no registo postal',
                          'una dirección concreta del registro postal',
                        )}
                  </span>
                  {/*
                    * The certificate, when one could be tied to this door.
                    *
                    * <p>Nothing is drawn when it could not. A door with no chip is a door the
                    * register did not match, which is not the same as a door with no certificate,
                    * and the list has no way to tell those apart - so it says neither.
                    *
                    * <p>Colour only on the expired one, which is the only one worth crossing the
                    * room for. A certificate that is simply valid gets the quiet treatment.
                    */}
                  <CertificateNote band={address.epcBand} validTo={address.epcValidTo} />
                </span>
                <span className="address-option__go">
                  {isOpening
                    ? tx('Opening…', 'Açılıyor…', 'Otwieranie…', 'A abrir…', 'Abriendo…')
                    : tx('View report', 'Raporu aç', 'Zobacz raport', 'Ver relatório', 'Ver informe')}
                  <ChevronRight size={16} aria-hidden="true" />
                </span>
              </button>

              <button
                type="button"
                className={
                  inComparison
                    ? 'button button--small button--added address-option__compare'
                    : 'button button--small button--ghost address-option__compare'
                }
                onClick={() => onToggleCompare(address)}
                disabled={compareBusy || opening}
                aria-pressed={inComparison}
              >
                {state === 'adding' ? (
                  tx('Adding…', 'Ekleniyor…', 'Dodawanie…', 'A juntar…', 'Añadiendo…')
                ) : state === 'removing' ? (
                  tx('Removing…', 'Çıkarılıyor…', 'Usuwanie…', 'A remover…', 'Quitando…')
                ) : inComparison ? (
                  <>
                    <span className="button__when-idle">
                      <Check size={15} aria-hidden="true" />
                      {tx('In comparison', 'Karşılaştırmada', 'W porównaniu', 'Na comparação', 'En la comparación')}
                    </span>
                    <span className="button__when-hover">
                      <X size={15} aria-hidden="true" />
                      {tx('Remove', 'Çıkar', 'Usuń', 'Remover', 'Quitar')}
                    </span>
                  </>
                ) : (
                  <>
                    <Plus size={15} aria-hidden="true" />
                    {tx('Compare', 'Karşılaştır', 'Porównaj', 'Comparar', 'Comparar')}
                  </>
                )}
                <span className="sr-only">
                  {tx(
                    ` — ${address.addressLine}`,
                    ` — ${address.addressLine}`,
                    ` — ${address.addressLine}`,
                    ` — ${address.addressLine}`,
                    ` — ${address.addressLine}`,
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {hidden > 0 ? (
        <button type="button" className="button button--secondary" onClick={() => setExpanded(true)}>
          {tx(
            `Show ${hidden} more address${hidden === 1 ? '' : 'es'}`,
            `${hidden} adres daha göster`,
            `Pokaż jeszcze ${hidden} ${hidden === 1 ? 'adres' : 'adresów'}`,
            `Mostrar mais ${hidden} morada${hidden === 1 ? '' : 's'}`,
            `Mostrar ${hidden} dirección${hidden === 1 ? '' : 'es'} más`,
          )}
        </button>
      ) : null}

      <p className="small muted">
        {tx(
          'Use the up and down arrows to move through the list.',
          'Listede gezinmek için yukarı ve aşağı ok tuşlarını kullanabilirsiniz.',
          'Do poruszania się po liście użyj strzałek w górę i w dół.',
          'Use as setas para cima e para baixo para percorrer a lista.',
          'Usa las flechas arriba y abajo para recorrer la lista.',
        )}
      </p>
    </section>
  );
}

/**
 * What the register says about this door's certificate, in one short line.
 *
 * <p>Three outcomes and only two of them say anything. A band with a lapsed expiry is worth
 * stopping for: the energy figures in that report describe the home as it was measured up to ten
 * years ago, and a home normally may not be let in England without a valid certificate. A band
 * that is current gets a quiet line, because a reader choosing between doors does not need it
 * emphasised. And nothing at all when no certificate could be tied to the door - saying "no
 * certificate" there would be a claim the join is not entitled to make.
 */
function CertificateNote({ band, validTo }: { band: string | null; validTo: string | null }) {
  if (!band) {
    return null;
  }
  const lapsed = hasLapsed(validTo);

  return (
    <span className={lapsed ? 'address-option__epc address-option__epc--lapsed' : 'address-option__epc'}>
      {lapsed ? <AlertTriangle size={13} aria-hidden="true" /> : null}
      {lapsed
        ? tx(
            `Band ${band} — certificate expired ${formatDate(validTo)}`,
            `${band} bandı — sertifikanın süresi ${formatDate(validTo)} tarihinde doldu`,
            `Klasa ${band} — świadectwo wygasło ${formatDate(validTo)}`,
            `Classe ${band} — certificado expirou em ${formatDate(validTo)}`,
            `Letra ${band} — certificado caducado el ${formatDate(validTo)}`,
          )
        : tx(
            `Band ${band} — certificate valid to ${formatDate(validTo)}`,
            `${band} bandı — sertifika ${formatDate(validTo)} tarihine kadar geçerli`,
            `Klasa ${band} — świadectwo ważne do ${formatDate(validTo)}`,
            `Classe ${band} — certificado válido até ${formatDate(validTo)}`,
            `Letra ${band} — certificado válido hasta ${formatDate(validTo)}`,
          )}
    </span>
  );
}
